import { ClientBase } from "./base.js";
import { MosqueClient } from "./mosque.js";
/** Dine client (#1084): guest table ordering, cash settle, kitchen line. */
export class DineClient extends MosqueClient {
    /** Public. A device (kiosk/board/kitchen) asks for a pairing code; staff bind it in the portal. */
    async startEdgeClaim(firmwareVersion = "", role) {
        return this.request("POST", "/v1/edge/claim", role ? { firmwareVersion, role } : { firmwareVersion });
    }
    /** Public, poll-secret gated. 204 -> pending; 200 -> claimed (device token delivered once). */
    async pollEdgeClaim(claimId, pollToken) {
        const res = await this.request("GET", `/v1/edge/claim/${encodeURIComponent(claimId)}/poll`, undefined, false, false, { "X-Lumora-Claim-Poll": pollToken });
        if (!res)
            return { status: "pending" };
        return { status: "claimed", deviceId: res.deviceId, token: res.token, ...(res.role ? { role: res.role } : {}) };
    }
    /** Public. The token travels in the body, never the URL. 404 for unknown/rotated/revoked. */
    async resolveTableTag(token) {
        return this.request("POST", "/v1/dining/tags/resolve", { token });
    }
    /** Public. Starts (201) or joins (200, joined:true) the anonymous table session. */
    async startTableSession(token) {
        return this.request("POST", "/v1/dining/tags/session", { token });
    }
    /**
     * POSTs the existing checkout route with `payCash: true`; answers with the
     * backend CashPlacementJSON shape (cashCode, expiresAt).
     */
    async placeCashOrder(slug, input, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/checkout`, { ...input, payCash: true }, false, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Staff (orders fulfill/manage). 404 cash_code_invalid, 429 rate_limited. */
    async settleCash(code) {
        return this.request("POST", "/v1/orders/settle-cash", { code }, true);
    }
    /**
     * Staff POS (kitchen auth + orders fulfill/manage). The server prices the order.
     * Pass `idempotencyKey` so a double-submit returns the same order. `pay: "card"` answers
     * 501 `not_implemented`; invalid items/modifiers/caps answer 400.
     */
    async placePosOrder(input, opts = {}) {
        return this.request("POST", `/v1/orders/pos${ClientBase.query({ location: opts.location })}`, input, true, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Staff POS menu with modifier groups, options and price deltas (display only). */
    async getPosCatalog(location) {
        return this.request("GET", `/v1/orders/pos/catalog${ClientBase.query({ location })}`, undefined, true);
    }
    async getKitchenLineStages(location) {
        return this.request("GET", `/v1/kitchen/line/stages${ClientBase.query({ location })}`, undefined, true);
    }
    async listKitchenLineTickets(opts = {}) {
        return this.request("GET", `/v1/kitchen/line/tickets${ClientBase.query({
            location: opts.location,
            include_ready: opts.includeReady ? "1" : undefined,
        })}`, undefined, true);
    }
    /** `fromStage` guards against a ticket that already moved (409 conflict). */
    async bumpKitchenTicket(orderId, opts = {}) {
        return this.kitchenAct(orderId, "bump", opts);
    }
    async unbumpKitchenTicket(orderId, opts = {}) {
        return this.kitchenAct(orderId, "unbump", opts);
    }
    async markKitchenTicketReady(orderId, opts = {}) {
        return this.kitchenAct(orderId, "ready", opts);
    }
    kitchenAct(orderId, action, opts) {
        const body = {};
        if (opts.fromStage)
            body["from_stage"] = opts.fromStage;
        if (opts.location !== undefined)
            body["location"] = opts.location;
        return this.request("POST", `/v1/kitchen/line/tickets/${encodeURIComponent(orderId)}/${action}`, body, true);
    }
    // ---- Stripe Terminal card payments (#1107): staff / kitchen-PIN / device token ----
    /** Starts a reader payment on a held order. 201 new, 200 replay of the same Idempotency-Key. */
    async startTerminalPayment(slug, orderId, input = {}, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/orders/${encodeURIComponent(orderId)}/terminal-payments`, input, true, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Stored state only; poll every 1-2s while processing. */
    async getTerminalPayment(slug, paymentId) {
        return this.request("GET", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}`, undefined, true);
    }
    /** 409 terminal_reader_busy when the reader is mid-authorization. */
    async cancelTerminalPayment(slug, paymentId) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/cancel`, undefined, true);
    }
    async retryTerminalPayment(slug, paymentId, input = {}) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/retry`, input, true);
    }
    /** Owner or platform admin only (billing:manage). */
    async listTerminalReaders(slug) {
        const r = await this.request("GET", `${this.siteBase(slug)}/terminal/readers`, undefined, true);
        return r.readers ?? [];
    }
}
//# sourceMappingURL=dine.js.map